import { WarningIcon } from '../icons'
import { IProduct, IProductResponse } from '../interfaces/product-interfaces'
import { Accordion } from './accordion'
import { Pillar } from './pillar'
import { SideSection } from './product-crosssell'
import { ProductImagesSlider } from './product-images-slider'
import { SpecialOrder } from './special-order'
import { Toasts } from './toasts'

const el = {
  root: document.querySelector<HTMLDivElement>('.product-layout'),
  sizesContainer: document.querySelector<HTMLDivElement>('.product-info__section--sizes'),
  screensContainer: document.querySelector<HTMLDivElement>(
    '.product-info__section--screens'
  ),
  colorsContainer: document.querySelector<HTMLDivElement>(
    '.product-info__section--colors'
  ),
  cpusContainer: document.querySelector<HTMLDivElement>('.product-info__section--cpus'),
  typesContainer: document.querySelector<HTMLDivElement>('.product-info__section--types'),
  availabilityContainer: document.querySelector<HTMLDivElement>(
    '.product-info__section--availability'
  ),
  crossSellContainer: document.querySelector<HTMLDivElement>(
    '.product-info__section--crossSell'
  ),
  crossSellSideSection: document.querySelector<HTMLDivElement>('.sidesection'),
  productTitle: document.querySelector<HTMLHeadingElement>('#product-title'),
  productSku: document.querySelector<HTMLParagraphElement>('#product-sku'),
  productDescription:
    document.querySelector<HTMLParagraphElement>('#product-description'),
  productSummary: document.querySelector<HTMLParagraphElement>('#product-summary'),
  productPrices: document.querySelector<HTMLDivElement>('#product-prices'),
  addToCartButton: document.querySelector<HTMLButtonElement>(
    '.product-layout .add-to-cart-form button'
  ),
  variantIdInputs: document.querySelectorAll<HTMLInputElement>(
    '.product-layout [name="variantId"], .product-bar__form [name="variantId"]'
  ),
  productSelectMessage: document.querySelector<HTMLLIElement>('#product-select-message'),
  productBar: document.querySelector<HTMLDivElement>('.product-bar'),
  specialOrderContainer: document.querySelector<HTMLDivElement>('#product-special-order'),
  moreInfoButton: document.querySelector<HTMLButtonElement>('#more-info-button'),
}

let state = {
  size: '',
  color: '',
  cpu: '',
  screen: '',
  type: '',
}

export const Product = {
  init: () => {
    if (!el.root) return
    Product.fetchProduct({ isInitial: true })
    window.addEventListener('popstate', () => {
      window.history.back()
    })
    if (el.moreInfoButton) {
      el.moreInfoButton.addEventListener('click', Product.onMoreInfoButtonClick)
    }
  },

  fetchProduct: async ({
    size = '',
    color = '',
    cpu = '',
    screen = '',
    type = '',
    isInitial = false,
    isColorChange = false,
  }) => {
    try {
      const { productid = '' } = el.root.dataset

      const queryParams = [
        `productId=${productid}`,
        `size=${size.replace('+', '%2B')}`,
        `color=${color.replace('+', '%2B')}`,
        `cpu=${cpu.replace('+', '%2B')}`,
        `type=${type.replace('+', '%2B')}`,
        `screen=${screen.replace('+', '%2B')}`,
        `colorChange=${isColorChange}`,
      ].join('&')

      const response = await fetch(`/api/variant/GetCombinations?${queryParams}`)
      const data: IProductResponse = await response.json()
      Product.setState(data)

      if (!isInitial) Product.updateHtml(data)

      Product.activateSizeOptions({ type: 'size' })
      Product.activateSizeOptions({ type: 'color' })
      Product.activateSizeOptions({ type: 'cpu' })
      Product.activateSizeOptions({ type: 'screen' })
      Product.activateSizeOptions({ type: 'type' })
    } catch (_) {
      if (isInitial) return
      Toasts.addToast({
        title: 'Villa kom upp!',
        text: 'Ekki tókst að velja vöruafbrigði.',
        icon: WarningIcon(),
      })
    }
  },

  setState: (data: IProductResponse) => {
    state = {
      size: el.sizesContainer
        ? data.combo.Sizes.find((x) => x.IsSelected)?.Title || ''
        : '',
      color: data.combo.Colors.find((x) => x.IsSelected)?.Title || '',
      screen: el.screensContainer
        ? data.combo.Screens.find((x) => x.IsSelected)?.Title || ''
        : '',
      cpu: data.combo.Cpu.find((x) => x.IsSelected)?.Title || '',
      type: data.combo.Types.find((x) => x.IsSelected)?.Title || '',
    }
  },

  activateSizeOptions: ({
    type = 'size',
  }: {
    type: 'size' | 'color' | 'cpu' | 'screen' | 'type'
  }) => {
    const options = document.querySelectorAll<HTMLInputElement>(
      `.product-layout [name="${type}"]`
    )
    options.forEach((target) =>
      target.addEventListener('change', (e) => Product.onOptionClick({ e, type }))
    )
  },

  onOptionClick: ({
    e,
    type = 'size',
  }: {
    e: Event
    type: 'size' | 'color' | 'cpu' | 'screen' | 'type'
  }) => {
    const target = e.target as HTMLInputElement
    Product.fetchProduct({
      ...state,
      [type]: target.value,
      isColorChange: type === 'color',
    })
  },

  updateHtml: (data: IProductResponse) => {
    if (el.sizesContainer) {
      el.sizesContainer.innerHTML = data.sizesHtml
    }
    if (el.crossSellContainer && data.crossSellHtml) {
      const newCrossSell = document.createElement('div')
      newCrossSell.innerHTML = data.crossSellHtml

      const newCategoryLinks = newCrossSell.querySelector(
        '.product-info__section--crossSell'
      )
      const newSideSection = newCrossSell.querySelector('.sidesection')

      el.crossSellContainer.innerHTML = newCategoryLinks.innerHTML
      el.crossSellSideSection.innerHTML = newSideSection.innerHTML
      newCrossSell.remove()

      const sidesectionCatLinks =
        el.crossSellContainer.querySelectorAll<HTMLAnchorElement>(
          '.product-category-link'
        )

      sidesectionCatLinks?.forEach((target) => {
        SideSection(target)
      })
    }
    if (!data.crossSellHtml) {
      el.crossSellContainer?.classList.add('h-hide')
    } else {
      el.crossSellContainer?.classList.remove('h-hide')
    }
    if (el.screensContainer) {
      el.screensContainer.innerHTML = data.screensHtml
    }
    if (el.colorsContainer) {
      el.colorsContainer.innerHTML = data.colorsHtml
    }
    if (el.cpusContainer) {
      el.cpusContainer.innerHTML = data.cpuHtml
    }
    if (el.typesContainer) {
      el.typesContainer.innerHTML = data.typesHtml
    }
    if (el.availabilityContainer) {
      el.availabilityContainer.innerHTML = data.availabilityHtml
    }
    if (el.specialOrderContainer) {
      Product.updateSpecialOrderBox(data)
    }
    if (el.productSku) {
      el.productSku.innerText = data.combo.Product.SKU
    }
    if (el.variantIdInputs.length && data.combo.Product.VariantId) {
      el.variantIdInputs.forEach(
        (target) => (target.value = data.combo.Product.VariantId)
      )
    }
    if (el.productSummary) {
      el.productSummary.innerHTML = data.combo.Product.Summary
    }
    if (el.productDescription) {
      el.productDescription.innerHTML = data.combo.Product.Description
    }
    if (el.productPrices) {
      el.productPrices.innerHTML = Product.generatePricesHtml(data.combo.Product)
    }
    Product.updateImages(data.imagesHtml)

    if (el.addToCartButton) {
      Product.updateAddToCartButton({
        hasStock: data.combo.Product.HasStock,
        isSpecialOrder: !!data.combo.SpecialOrders,
        isPreOrder: data.combo.Product.IsPreOrder,
      })
    }
    if (el.productSelectMessage) {
      Product.updateSelectMessage(data)
    }
    if (el.productBar) {
      Product.updateProductBar(data)
    }

    Product.shallowUpdateUrl(data.combo.Product.SKU)
  },

  generatePricesHtml: (product: IProduct): string => {
    if (product.HasDiscount || product.HasSpecialPrice) {
      return `
        <p class="product-info__price product-info__price--old h-flex-wrap-item">${product.OriginalPrice}</p>
        <p class="product-info__price product-info__price--discount h-flex-wrap-item">${product.Price}</p>
      `
    }

    return `<p class="product-info__price h-flex-wrap-item">${product.Price}</p>`
  },

  updateAddToCartButton: ({
    hasStock = true,
    isSpecialOrder = false,
    isPreOrder = false,
  }) => {
    const buttonText = el.addToCartButton.querySelector<HTMLSpanElement>('.button__text')

    if (buttonText) {
      buttonText.innerText = isPreOrder
        ? 'Forpanta'
        : isSpecialOrder
        ? 'Sérpanta'
        : hasStock
        ? 'Bæta við körfu'
        : 'Vara ekki til á lager'
    }
    el.addToCartButton.disabled = !hasStock

    const stockNotification = document.querySelector<HTMLDivElement>(
      '.stockNotification-card'
    )

    if (hasStock) {
      if (stockNotification) {
        stockNotification.style.display = 'none'
      }
    } else {
      if (stockNotification) {
        stockNotification.style.display = 'block'
      }
    }
  },

  // there are type options but none is selected -> display select message and disable cart button
  updateSelectMessage: (data: IProductResponse) => {
    const displayMessage =
      data.combo.Types.length && !data.combo.Types.some((x) => x.IsSelected)

    // return if message should not be displayed
    if (!displayMessage) {
      el.productSelectMessage.classList.add('product-info__item--hide')
      return
    }

    const message = displayMessage ? 'Veldu þína tegund' : ''

    const textElement = el.productSelectMessage.querySelector<HTMLParagraphElement>('p')
    if (textElement) textElement.innerText = message

    if (message.length) {
      el.productSelectMessage.classList.remove('product-info__item--hide')
      el.addToCartButton.disabled = true
      return
    }

    el.productSelectMessage.classList.add('product-info__item--hide')
    el.addToCartButton.disabled = false
  },

  updateProductBar: (data: IProductResponse) => {
    const price = el.productBar.querySelector<HTMLParagraphElement>('.product-bar__price')
    const priceReductionPillar = document.querySelector<HTMLDivElement>('.priceReduction')
    const button = el.productBar.querySelector<HTMLButtonElement>('.product-bar__button')
    if (!price || !button) return

    const noTypeSelected =
      data.combo.Types.length && !data.combo.Types.some((x) => x.IsSelected)
    price.innerHTML = data.combo.Product.Price

    if (data.combo.Product.HasDiscount || data.combo.Product.HasSpecialPrice) {
      price.classList.add('text--purple')
    } else {
      price.classList.remove('text--purple')
    }

    if (data.combo.Product.PriceReduction) {
      priceReductionPillar.classList.remove('h-hide')
    } else {
      if (!priceReductionPillar.classList.contains('h-hide')) {
        priceReductionPillar.classList.add('h-hide')
      }
    }

    const disableButton = !data.combo.Product.HasStock || noTypeSelected
    button.disabled = disableButton

    const buttonTextElement = button.querySelector<HTMLSpanElement>('.button__text')
    if (!buttonTextElement) return
    buttonTextElement.innerText = data.combo.Product.IsPreOrder
      ? 'Forpanta'
      : data.combo.SpecialOrders
      ? 'Sérpanta'
      : 'Kaupa'
  },

  shallowUpdateUrl: (sku = '') => {
    const url = new URL(window.location.href)
    url.searchParams.set('sku', sku)
    window.history.pushState({}, '', url)
  },

  updateSpecialOrderBox: (data: IProductResponse) => {
    if (!data.combo.SpecialOrders) {
      el.specialOrderContainer.classList.remove('product-info__special-order--visible')
      return
    }

    // activate special order box
    el.specialOrderContainer.classList.add('product-info__special-order--visible')
    el.specialOrderContainer.innerHTML = data.specialOrderHtml
    SpecialOrder.activateSpecialOrderBox()

    // activate accordion
    const accordion = el.specialOrderContainer.querySelector<HTMLDivElement>('.accordion')
    if (accordion) Accordion(accordion)

    // activate pillars
    const pillars = el.specialOrderContainer.querySelectorAll<HTMLLabelElement>('.pillar')
    pillars.forEach((target) => Pillar(target))

    // update key attribute on product-layout
    const productLayout = document.querySelector<HTMLDivElement>('.product-layout')
    if (productLayout)
      productLayout.setAttribute('data-productkey', data.combo.Product.VariantId)
  },

  updateImages: (imagesHtml = '') => {
    const productImage = document.querySelector<HTMLImageElement>('#product-image')
    if (!productImage || !imagesHtml) return

    const images = document.createElement('div')
    images.innerHTML = imagesHtml.trim()
    const imagesContent = images.firstChild

    productImage.replaceWith(imagesContent)
    ProductImagesSlider.init()
  },

  onMoreInfoButtonClick: () => {
    const hasContent = document.querySelectorAll<HTMLDivElement>(
      '#product-more-info .section, #product-more-info section'
    ).length
    const target = document.querySelector<HTMLDivElement>('.product-layout-anchor')
    if (hasContent && target) target.scrollIntoView()
  },
}
